import * as React from 'react'
import { parseAsString, useQueryState } from 'nuqs'

import { RefreshCwIcon, Button } from '@buffer-mono/popcorn'

import { ActionButton } from '~publish/legacy/ai/components/AIAssistant/components/ActionButton'
import { CopyButton } from '~publish/legacy/ai/components/AIAssistant/components/CopyButton'
import { Error } from '~publish/legacy/ai/components/AIAssistant/components/Error'
import { FunFact } from '~publish/legacy/ai/components/AIAssistant/components/FunFact'
import { Loader } from '~publish/legacy/ai/components/AIAssistant/components/Loader'
import { RetryButton } from '~publish/legacy/ai/components/AIAssistant/components/RetryButton'
import { ToneButton } from '~publish/legacy/ai/components/AIAssistant/components/ToneButton'
import {
  resetContentGeneration,
  resetSelection,
  selectContentGenerationStatus,
} from '~publish/legacy/ai/state/slice'
import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'

import { peekComposerStore } from '~publish/components/Composer/store/composerStore'

import { AIAssistantContext } from '../AIAssistant'

import styles from './GenerationResult.module.css'

import type { JSX } from 'react'

/**
 *
 */
function GenerationResult({ disabled }: { disabled?: boolean }): JSX.Element {
  const dispatch = useAppDispatch()
  const [, setAiAssisted] = useQueryState(
    'aiAssisted',
    parseAsString.withOptions({ history: 'replace' }),
  )

  const { editor } = React.useContext(AIAssistantContext)

  const {
    contentGenerationInProgress,
    contentGenerationError,
    contentGenerationResults,
  } = useAppSelector((state) => selectContentGenerationStatus(state))

  const onAddContent = (): void => {
    editor?.addGeneratedContent?.({ content })
    dispatch(resetContentGeneration())
    dispatch(resetSelection())

    // For Composer: handles setting all drafts as AI assisted.
    ComposerActionCreators.updateAiAssist()
    // peekComposerStore: this handler also runs from the Ideas Editor, where
    // no composer session exists.
    peekComposerStore()?.getState().setOmniAiAssisted(true)

    // For Ideas Editor: handles setting current idea as AI assisted.
    // When content is inserted/replaced, we add 'aiAssisted' query param to the URL
    // with 'replace' to ensure back navigation still operates.
    void setAiAssisted('true')
  }

  const content = contentGenerationResults[0]

  const showLoader = contentGenerationInProgress
  const showError = contentGenerationError
  const showContent = content && !showLoader && !showError

  const shouldReplace = editor && editor?.getSelection?.()

  return (
    <>
      <div className={styles.verticalWrapper}>
        {contentGenerationError && <Error />}

        {showContent && (
          <div className={styles.wrapper}>
            <div
              className={`${styles.contentWrapper} ${
                disabled ? styles.disabled : ''
              }`}
            >
              <pre
                className={`${styles.content} ${
                  disabled ? styles.disabled : ''
                }`}
                data-testid="ai-assistant-content-generated"
                translate="no"
              >
                {content.trim()}
              </pre>
              <div className={styles.buttonWrapper}>
                <CopyButton disabled={disabled} />
                <div className={styles.primaryButtonsWrapper}>
                  <RetryButton disabled={disabled}>
                    <RefreshCwIcon />
                    Retry
                  </RetryButton>
                  <Button
                    onClick={onAddContent}
                    disabled={disabled}
                    className={styles.insertButton}
                  >
                    {shouldReplace ? 'Replace' : 'Insert'}
                  </Button>
                </div>
              </div>
            </div>
            <div
              className={styles.actionsWrapper}
              id="ai-assistant-fine-tuning-container"
            >
              {['rephrase', 'shorten', 'expand'].map((action) => (
                <ActionButton
                  target={content}
                  action={action}
                  disabled={disabled}
                  key={action}
                  testId={`ai-assistant-fine-tuning-${action}-button`}
                  secondary
                />
              ))}
              {['casual', 'formal'].map((tone) => (
                <ToneButton
                  tone={tone}
                  disabled={disabled}
                  key={tone}
                  testId={`ai-assistant-fine-tuning-${tone}-button`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {contentGenerationInProgress && (
        <div className={styles.loadingWrapper}>
          <Loader />
          <FunFact />
        </div>
      )}
    </>
  )
}

export { GenerationResult }
